import type { SiteScopedRole } from '../schemas/shiftbook.js';

export const SHIFTBOOK_ENTRY_PERMISSION_ACTIONS = [
  'shiftbook.entry.edit_details',
  'shiftbook.entry.manage_checklist',
  'shiftbook.entry.manage_attachments',
  'shiftbook.entry.comment',
  'shiftbook.entry.complete',
  'shiftbook.entry.reopen',
  'shiftbook.entry.delete',
] as const;

export type ShiftbookEntryPermissionAction = (typeof SHIFTBOOK_ENTRY_PERMISSION_ACTIONS)[number];

export const SHIFTBOOK_ENTRY_PERMISSION_DECISION_REASONS = [
  'allowed_admin',
  'allowed_supervisor',
  'allowed_owner',
  'allowed_assignee',
  'allowed_mutation_role_comment',
  'denied_insufficient_role',
  'denied_not_owner_or_assigned',
  'denied_invalid_status',
  'denied_policy_rule',
] as const;

export type ShiftbookEntryPermissionDecisionReason =
  (typeof SHIFTBOOK_ENTRY_PERMISSION_DECISION_REASONS)[number];

export type ShiftbookEntryPermissionContext = {
  actor: {
    id: string | null;
    role: 'viewer' | 'editor' | 'supervisor' | 'admin' | null;
    site: 'aar' | 'fra' | null;
  };
  viewerGroupIds: string[];
  entry: {
    id: string;
    site: 'aar' | 'fra';
    ownerUserId: string;
    responsible: { kind: 'user' | 'group'; id: string } | null;
    status: 'open' | 'in_progress' | 'completed';
  };
};

export type ShiftbookEntryPermissionDecision = {
  action: ShiftbookEntryPermissionAction;
  allowed: boolean;
  reason: ShiftbookEntryPermissionDecisionReason;
};

export type ShiftbookEntryPermissionDecisions = {
  editDetails: ShiftbookEntryPermissionDecision;
  manageChecklist: ShiftbookEntryPermissionDecision;
  manageAttachments: ShiftbookEntryPermissionDecision;
  comment: ShiftbookEntryPermissionDecision;
  complete: ShiftbookEntryPermissionDecision;
  reopen: ShiftbookEntryPermissionDecision;
  delete: ShiftbookEntryPermissionDecision;
};

export type ShiftbookEntryPermissions = {
  canEditDetails: boolean;
  canManageChecklist: boolean;
  canManageAttachments: boolean;
  canComment: boolean;
  canComplete: boolean;
  canReopen: boolean;
  canDelete: boolean;
};

export const DEFAULT_SHIFTBOOK_ENTRY_PERMISSIONS: ShiftbookEntryPermissions = {
  canEditDetails: false,
  canManageChecklist: false,
  canManageAttachments: false,
  canComment: false,
  canComplete: false,
  canReopen: false,
  canDelete: false,
};

export function canCreateShiftbookEntry(
  role?: ShiftbookEntryPermissionContext['actor']['role'],
): boolean {
  return role === 'editor' || role === 'supervisor' || role === 'admin';
}

export function isPrivilegedRole(
  role: SiteScopedRole | null | undefined,
): role is 'supervisor' | 'admin' {
  return role === 'supervisor' || role === 'admin';
}

function isOwner(context: ShiftbookEntryPermissionContext): boolean {
  return Boolean(context.actor.id && context.actor.id === context.entry.ownerUserId);
}

function isAssignee(context: ShiftbookEntryPermissionContext): boolean {
  const responsible = context.entry.responsible;
  if (!responsible) {
    return false;
  }

  // Case-insensitive id compare, consistent with the rest of the
  // responsible-party plumbing: SQL reads return uppercase uniqueidentifiers
  // while payload-sourced ids are lowercase.
  const responsibleId = responsible.id.toLowerCase();
  return responsible.kind === 'user'
    ? Boolean(context.actor.id && context.actor.id.toLowerCase() === responsibleId)
    : context.viewerGroupIds.some((groupId) => groupId.toLowerCase() === responsibleId);
}

function hasActorIdentity(context: ShiftbookEntryPermissionContext): boolean {
  return Boolean(context.actor.id);
}

function hasConsistentSite(context: ShiftbookEntryPermissionContext): boolean {
  return context.actor.site === null || context.actor.site === context.entry.site;
}

function allow(
  action: ShiftbookEntryPermissionAction,
  reason: ShiftbookEntryPermissionDecisionReason,
): ShiftbookEntryPermissionDecision {
  return { action, allowed: true, reason };
}

function deny(
  action: ShiftbookEntryPermissionAction,
  reason: ShiftbookEntryPermissionDecisionReason,
): ShiftbookEntryPermissionDecision {
  return { action, allowed: false, reason };
}

function computeOwnerScopedDecision(
  context: ShiftbookEntryPermissionContext,
  action: ShiftbookEntryPermissionAction,
): ShiftbookEntryPermissionDecision {
  if (!hasConsistentSite(context)) {
    return deny(action, 'denied_policy_rule');
  }

  const { role } = context.actor;
  if (isPrivilegedRole(role)) {
    return allow(action, role === 'admin' ? 'allowed_admin' : 'allowed_supervisor');
  }

  if (role !== 'editor') {
    return deny(action, 'denied_insufficient_role');
  }

  if (!hasActorIdentity(context)) {
    return deny(action, 'denied_policy_rule');
  }

  if (isOwner(context)) {
    return allow(action, 'allowed_owner');
  }

  if (isAssignee(context)) {
    return allow(action, 'allowed_assignee');
  }

  return deny(action, 'denied_not_owner_or_assigned');
}

function computeCommentDecision(
  context: ShiftbookEntryPermissionContext,
): ShiftbookEntryPermissionDecision {
  const action = 'shiftbook.entry.comment';

  if (!hasConsistentSite(context)) {
    return deny(action, 'denied_policy_rule');
  }

  const { role } = context.actor;
  if (role === 'admin') {
    return allow(action, 'allowed_admin');
  }
  if (role === 'supervisor') {
    return allow(action, 'allowed_supervisor');
  }
  if (role === 'editor') {
    return hasActorIdentity(context)
      ? allow(action, 'allowed_mutation_role_comment')
      : deny(action, 'denied_policy_rule');
  }
  return deny(action, 'denied_insufficient_role');
}

function computeStatusDecision(
  context: ShiftbookEntryPermissionContext,
  action: 'shiftbook.entry.complete' | 'shiftbook.entry.reopen',
): ShiftbookEntryPermissionDecision {
  const baseDecision = computeOwnerScopedDecision(context, action);
  if (!baseDecision.allowed) {
    return baseDecision;
  }

  if (action === 'shiftbook.entry.complete') {
    return context.entry.status === 'open' || context.entry.status === 'in_progress'
      ? baseDecision
      : deny(action, 'denied_invalid_status');
  }

  return context.entry.status === 'completed'
    ? baseDecision
    : deny(action, 'denied_invalid_status');
}

function computeShiftbookEntryPermissionDecision(
  context: ShiftbookEntryPermissionContext,
  action: ShiftbookEntryPermissionAction,
): ShiftbookEntryPermissionDecision {
  switch (action) {
    case 'shiftbook.entry.edit_details':
    case 'shiftbook.entry.manage_checklist':
    case 'shiftbook.entry.manage_attachments':
    case 'shiftbook.entry.delete':
      return computeOwnerScopedDecision(context, action);
    case 'shiftbook.entry.comment':
      return computeCommentDecision(context);
    case 'shiftbook.entry.complete':
    case 'shiftbook.entry.reopen':
      return computeStatusDecision(context, action);
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

export function computeShiftbookEntryPermissionDecisions(
  context: ShiftbookEntryPermissionContext,
): ShiftbookEntryPermissionDecisions {
  return {
    editDetails: computeShiftbookEntryPermissionDecision(context, 'shiftbook.entry.edit_details'),
    manageChecklist: computeShiftbookEntryPermissionDecision(
      context,
      'shiftbook.entry.manage_checklist',
    ),
    manageAttachments: computeShiftbookEntryPermissionDecision(
      context,
      'shiftbook.entry.manage_attachments',
    ),
    comment: computeShiftbookEntryPermissionDecision(context, 'shiftbook.entry.comment'),
    complete: computeShiftbookEntryPermissionDecision(context, 'shiftbook.entry.complete'),
    reopen: computeShiftbookEntryPermissionDecision(context, 'shiftbook.entry.reopen'),
    delete: computeShiftbookEntryPermissionDecision(context, 'shiftbook.entry.delete'),
  };
}

export function computeShiftbookEntryPermissions(
  context: ShiftbookEntryPermissionContext,
): ShiftbookEntryPermissions {
  const decisions = computeShiftbookEntryPermissionDecisions(context);
  return {
    canEditDetails: decisions.editDetails.allowed,
    canManageChecklist: decisions.manageChecklist.allowed,
    canManageAttachments: decisions.manageAttachments.allowed,
    canComment: decisions.comment.allowed,
    canComplete: decisions.complete.allowed,
    canReopen: decisions.reopen.allowed,
    canDelete: decisions.delete.allowed,
  };
}
